import React, { useState } from 'react';

import { CreateEntityInput, EntityTypeEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ALL_SERVICE, GLOBAL_TERRITORY } from 'src/constants/context';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IToast } from 'src/types/IToaster';
import { IVendor } from 'src/types/IVendor';

import { ConfirmationModal } from '../../ConfirmationModal';
import { SubmissionSettings } from '../SubmissionSettings';
import { mapVendorCodeOverrides, mapVendorCodes } from '../mappers/mapVendorCodes';
import './ConfirmNewVendor.scss';
import { VendorCodeOverridesGrid } from './VendorCodeOverridesGrid';
import { VendorCodesGrid } from './VendorCodesGrid';

interface IConfirmNewVendorProps {
    vendor: IVendor;
    isEditMode: boolean;
    onPrevious: () => void;
    openCancelModal: () => void;
}

export const ConfirmNewVendor = ({ vendor, isEditMode, onPrevious, openCancelModal }: IConfirmNewVendorProps) => {
    const { metadata, setToasts, setTopAlertMsg, userInfo, setEntityRefreshCounter } = React.useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmAddVendorModalOpen, setIsConfirmAddVendorModalOpen] = useState(false);
    const [comments, setComments] = useState('');
    const history = useHistory();
    const services = Object.values(metadata.serviceMapping).filter((service) => service.serviceId !== ALL_SERVICE.ID);
    const territories = Object.values(metadata.territoryMapping).filter(
        (territory) => territory.territoryId !== GLOBAL_TERRITORY.ID
    );

    const handleConfirmAddVendor = () => {
        if (isEditMode) {
            editEntity();
        } else {
            createEntity();
        }
    };

    const createEntity = () => {
        setIsLoading(true);
        contractConfigurationClient
            .createEntity(CreateEntityInput.clone(mapVendorToEntity()))
            .subscribe(
                () => {
                    const toast: IToast = {
                        id: 'add-vendor-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT,
                        message: '1 vendor added'
                    };
                    setToasts([toast]);
                    setEntityRefreshCounter((prev) => prev + 1);
                    history.push(ROUTES.VENDORS.INDEX);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('createEntity unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            )
            .add(() => setIsLoading(false));
    };

    const editEntity = () => {
        setIsLoading(true);
        contractConfigurationClient
            .editEntity(CreateEntityInput.clone(mapVendorToEntity()))
            .subscribe(
                () => {
                    const toast: IToast = {
                        id: 'edit-vendor-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT,
                        message: 'Vendor details updated'
                    };
                    setToasts([toast]);
                    setEntityRefreshCounter((prev) => prev + 1);
                    history.push(`${ROUTES.VENDORS.INDEX}/${vendor.vendorId}`);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('editEntity unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            )
            .add(() => setIsLoading(false));
    };

    const mapVendorToEntity = () => {
        const entityForCreate = {
            createdBy: userInfo.username,
            defaultEntityVendorCodes: mapVendorCodes(vendor.vendorCodes),
            entityName: vendor.vendorName,
            entityType: EntityTypeEnum.Label,
            notes: vendor.description,
            overrideEntityVendorCodes: mapVendorCodeOverrides(vendor.vendorCodeOverrides, services, territories),
            submitAccruals: vendor.submitAccruals,
            submitPayments: vendor.submitPayments
        } as CreateEntityInput;

        const entityForEdit = {
            updatedBy: userInfo.username,
            defaultEntityVendorCodes: mapVendorCodes(vendor.vendorCodes),
            entityId: vendor.vendorId,
            entityName: vendor.vendorName,
            entityType: EntityTypeEnum.Label,
            notes: vendor.description,
            overrideEntityVendorCodes: mapVendorCodeOverrides(vendor.vendorCodeOverrides, services, territories),
            submitAccruals: vendor.submitAccruals,
            submitPayments: vendor.submitPayments
        } as CreateEntityInput;

        return isEditMode ? entityForEdit : entityForCreate;
    };

    return (
        <Column spacing="500">
            <Heading className="confirm-vendor-heading" level={2}>
                {isEditMode ? 'Confirm vendor edits' : 'Confirm new vendor'}
            </Heading>
            <Column spacing="300">
                <Heading level={1}>{vendor.vendorName}</Heading>
                <Text>{vendor.description}</Text>
            </Column>
            <VendorCodesGrid vendorCodes={vendor.vendorCodes} />
            <VendorCodeOverridesGrid vendorCodeOverrides={vendor.vendorCodeOverrides} />
            <SubmissionSettings vendor={vendor} isEditMode={false} />
            <Row alignmentHorizontal="end">
                <Button onClick={openCancelModal} type="link" minWidth={174}>
                    Cancel
                </Button>
                <Button onClick={onPrevious} type="tertiary" minWidth={174}>
                    Previous
                </Button>
                <Button onClick={() => setIsConfirmAddVendorModalOpen(true)} minWidth={174}>
                    {isEditMode ? 'Save vendor' : 'Add vendor'}
                </Button>
            </Row>
            <ConfirmationModal
                isOpen={isConfirmAddVendorModalOpen}
                onClose={() => setIsConfirmAddVendorModalOpen(false)}
                title={isEditMode ? 'Confirm edits to vendor' : 'Confirm add vendor?'}
                body={
                    isEditMode
                        ? 'Are you sure you want to make these edits?'
                        : 'Are you sure you want to add new vendor?'
                }
                warning="Edits may require reruns for past accounting periods."
                showWarning={isEditMode}
                comments={comments}
                showComments={true}
                onCommentsChange={setComments}
                primaryButtonText={isEditMode ? 'Yes, save edits' : 'Add vendor'}
                primaryButtonOnClick={handleConfirmAddVendor}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsConfirmAddVendorModalOpen(false)}
                isLoading={isLoading}
            />
        </Column>
    );
};
